import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { ArrowRight, Clock, Calendar, Tag, ChevronLeft } from 'lucide-react'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

const categoryColors = {
  'IT Infrastructure': 'bg-blue-50 text-blue-700 border-blue-200',
  'Security & Infrastructure': 'bg-slate-50 text-slate-700 border-slate-200',
  'Business Technology': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Case Study': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Software Development': 'bg-violet-50 text-violet-700 border-violet-200',
}

function getCategoryStyle(cat) {
  return categoryColors[cat] || 'bg-[#EBF2FF] text-[#0047AB] border-[#0047AB]/20'
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })
}

export default function Blog() {
  const [posts, setPosts] = useState([])
  const [categories, setCategories] = useState([])
  const [activeCategory, setActiveCategory] = useState('All')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      fetch(`${API}/blog`).then(r => r.json()),
      fetch(`${API}/blog/categories`).then(r => r.json()),
    ]).then(([postsData, catsData]) => {
      setPosts(postsData)
      setCategories(['All', ...catsData])
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  const filtered = activeCategory === 'All' ? posts : posts.filter(p => p.category === activeCategory)
  const featured = filtered.filter(p => p.featured)
  const rest = filtered.filter(p => !p.featured)

  return (
    <>
      <Helmet>
        <title>IT Blog & Insights | Akshay Rao — Melbourne IT Specialist</title>
        <meta name="description" content="Practical IT guides for Melbourne small businesses — CCTV installation, POS systems, business email setup, digital ordering apps and more. Written by Akshay Rao." />
        <meta property="og:title" content="IT Blog & Insights | Akshay Rao Melbourne" />
        <meta property="og:description" content="Practical IT guides for Melbourne small businesses — CCTV, POS, email, web apps and more." />
        <link rel="canonical" href="https://akshayrao.com.au/blog" />
      </Helmet>

      <div className="min-h-screen bg-[#FAFAFA]">
        {/* Header */}
        <div className="bg-white border-b border-[#E5E7EB] pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-12 md:py-16">
            <Link to="/" className="inline-flex items-center gap-2 text-[#6B7280] text-sm font-body hover:text-[#0047AB] transition-colors mb-6">
              <ChevronLeft size={14} /> Back to Portfolio
            </Link>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="flex items-center gap-3 mb-4">
                <span className="font-mono text-[#0047AB] text-xs tracking-[0.3em] uppercase">Blog</span>
                <span className="h-px w-12 bg-[#0047AB]/25" />
              </div>
              <h1 className="font-sans font-bold text-4xl md:text-5xl tracking-tight text-[#111111] mb-3">
                IT Insights from the Field
              </h1>
              <p className="font-body text-[#6B7280] text-base md:text-lg max-w-xl">
                Practical guides for Melbourne small businesses — from someone who actually installs, builds and supports IT every day.
              </p>
            </motion.div>

            {/* Category filter */}
            {categories.length > 1 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
                className="flex flex-wrap gap-2 mt-8">
                {categories.map(cat => (
                  <button key={cat} onClick={() => setActiveCategory(cat)}
                    className={`px-4 py-1.5 rounded-full text-sm font-body border transition-all duration-200 ${
                      activeCategory === cat
                        ? 'bg-[#0047AB] text-white border-[#0047AB]'
                        : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#0047AB]/40 hover:text-[#374151]'
                    }`}>
                    {cat}
                  </button>
                ))}
              </motion.div>
            )}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-12 md:py-16">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1,2,3].map(i => <div key={i} className="h-64 bg-[#F3F4F6] rounded-2xl animate-pulse" />)}
            </div>
          ) : (
            <>
              {/* Featured posts */}
              {featured.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  {featured.map((post, i) => (
                    <PostCard key={post.slug} post={post} featured index={i} />
                  ))}
                </div>
              )}
              {/* Rest of posts */}
              {rest.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {rest.map((post, i) => (
                    <PostCard key={post.slug} post={post} index={i + featured.length} />
                  ))}
                </div>
              )}
              {filtered.length === 0 && (
                <div className="text-center py-16 text-[#6B7280] font-body">No posts in this category yet.</div>
              )}
            </>
          )}

          {/* CTA */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="mt-16 p-8 bg-white border border-[#E5E7EB] rounded-2xl shadow-card flex flex-col md:flex-row items-start md:items-center gap-5">
            <div className="flex-1">
              <div className="font-sans font-bold text-[#111111] text-lg mb-1">Need IT help for your Melbourne business?</div>
              <div className="font-body text-[#6B7280] text-sm">Book a 30-minute consultation from $8.99 and get a clear plan.</div>
            </div>
            <Link to="/#contact"
              className="shrink-0 flex items-center gap-2 px-6 py-3 bg-[#0047AB] text-white text-sm font-semibold rounded-xl hover:bg-[#003d94] transition-colors">
              Book a consultation <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>
      </div>
    </>
  )
}

function PostCard({ post, featured, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
    >
      <Link to={`/blog/${post.slug}`}
        className="group flex flex-col h-full bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-card hover:shadow-card-hover hover:border-[#0047AB]/20 transition-all duration-300"
        data-testid={`blog-card-${post.slug}`}
      >
        {/* Category */}
        <div className="flex items-center justify-between mb-4">
          <span className={`px-2.5 py-1 text-xs font-mono border rounded-full ${getCategoryStyle(post.category)}`}>
            {post.category}
          </span>
          {featured && (
            <span className="px-2 py-0.5 text-[9px] font-mono uppercase tracking-wider text-[#0047AB] bg-[#EBF2FF] border border-[#0047AB]/20 rounded-full">
              Featured
            </span>
          )}
        </div>

        {/* Title */}
        <h2 className="font-sans font-bold text-[#111111] text-lg leading-snug mb-3 group-hover:text-[#0047AB] transition-colors duration-200 flex-1">
          {post.title}
        </h2>

        {/* Excerpt */}
        <p className="font-body text-[#6B7280] text-sm leading-relaxed mb-5 line-clamp-3">
          {post.excerpt}
        </p>

        {/* Tags */}
        {post.tags?.slice(0, 3).map(tag => (
          <span key={tag} className="inline-flex items-center gap-1 mr-1.5 mb-3 text-[9px] font-mono text-[#9CA3AF] uppercase tracking-wider">
            <Tag size={8} />{tag}
          </span>
        ))}

        {/* Meta */}
        <div className="flex items-center justify-between pt-4 border-t border-[#F3F4F6]">
          <div className="flex items-center gap-3 text-xs text-[#9CA3AF] font-body">
            <span className="flex items-center gap-1"><Calendar size={11} />{formatDate(post.created_at)}</span>
            <span className="flex items-center gap-1"><Clock size={11} />{post.read_time} min read</span>
          </div>
          <span className="flex items-center gap-1 text-[#0047AB] text-xs font-mono group-hover:gap-2 transition-all">
            Read <ArrowRight size={11} />
          </span>
        </div>
      </Link>
    </motion.div>
  )
}
